import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'

import Loading from '../ui/Loading'
import { useNotifications } from '../ui/NotificationCenter'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'

export default function DashboardPage() {
    const navigate = useNavigate()
    const { hasAnyRole } = useAuth()
    const { notifications, dismiss } = useNotifications()

    // 역할 그룹
    const isOperator = hasAnyRole([Roles.OPERATOR])
    const isGaAdmin = hasAnyRole([Roles.GA_LEADER, Roles.GA_BRANCH_MANAGER, Roles.GA_DEPUTY_BRANCH_MANAGER, Roles.GA_MANAGER])
    const isInsAdmin = hasAnyRole([Roles.INSURER_LEADER, Roles.INSURER_BRANCH_MANAGER, Roles.INSURER_DEPUTY_BRANCH_MANAGER])
    const isGaPlanner = hasAnyRole([Roles.GA_PLANNER])
    const isInsManager = hasAnyRole([Roles.INSURER_MANAGER, Roles.INSURER_EDUCATION_MANAGER, Roles.INSURER_PLAN_MANAGER])
    const isGaUser = hasAnyRole([
        Roles.GA_MASTER_ADMIN,
        Roles.GA_MASTER_DEPUTY_ADMIN,
        Roles.GA_LEADER,
        Roles.GA_DEPUTY_LEADER,
        Roles.GA_BRANCH_MANAGER,
        Roles.GA_DEPUTY_BRANCH_MANAGER,
        Roles.GA_OTHER_ADMIN,
        Roles.GA_MANAGER,
        Roles.GA_PLANNER,
        Roles.GA_ETC,
    ])

    const isAdminView = isOperator || isGaAdmin || isInsAdmin
    const isGaPlannerView = !isAdminView && isGaPlanner
    const isInsManagerView = !isAdminView && !isGaPlannerView && isInsManager

    // 근무 상태 위젯은 관리자급(백엔드 /manager-status/active 권한과 동일)만 노출
    const canViewActiveStatus = hasAnyRole([
        Roles.OPERATOR,
        Roles.GA_LEADER,
        Roles.GA_BRANCH_MANAGER,
        Roles.GA_DEPUTY_BRANCH_MANAGER,
        Roles.INSURER_LEADER,
        Roles.INSURER_BRANCH_MANAGER,
        Roles.INSURER_DEPUTY_BRANCH_MANAGER,
    ])
    const [kpi, setKpi] = useState({
        new: '-',
        fix: '-',
        done: '-',
        avgDays: '-',
        total: '-',
        pending: '-',
        inProgress: '-',
        completed: '-',
        completionRate: '-'
    })
    const [series, setSeries] = useState([])
    const [orgId, setOrgId] = useState('')
    const [orgOptions, setOrgOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [activeOnDuty, setActiveOnDuty] = useState([])
    const [activeLoading, setActiveLoading] = useState(false)

    // 공지: 분류별(우리조직/원수사) 카운트
    const [noticeInternalUnreadCount, setNoticeInternalUnreadCount] = useState(0)
    const [noticeInsurerUnreadCount, setNoticeInsurerUnreadCount] = useState(0)
    const [noticeApprovalNeededCount, setNoticeApprovalNeededCount] = useState(0)
    const canApproveNotice = hasAnyRole([
        Roles.OPERATOR,
        Roles.GA_MASTER_ADMIN,
        Roles.GA_MASTER_DEPUTY_ADMIN,
        Roles.GA_LEADER,
        Roles.GA_DEPUTY_LEADER,
        Roles.GA_OTHER_ADMIN,
        Roles.GA_BRANCH_MANAGER,
        Roles.GA_DEPUTY_BRANCH_MANAGER,
        Roles.INSURER_MASTER_ADMIN,
        Roles.INSURER_MASTER_DEPUTY_ADMIN,
        Roles.INSURER_LEADER,
        Roles.INSURER_DEPUTY_LEADER,
        Roles.INSURER_OTHER_ADMIN,
        Roles.INSURER_BRANCH_MANAGER,
        Roles.INSURER_DEPUTY_BRANCH_MANAGER,
    ])
    const myNoticeOrgType = isGaAdmin || isGaPlannerView ? 'GA' : (isInsAdmin || isInsManagerView ? 'INSURER' : '')

    // 근무 상태: 가입 승인 대기 인원
    const [signupPendingCount, setSignupPendingCount] = useState(0)

    // 조직 선택 옵션 로딩 (로그인 사용자의 조직 + 하위 조직)
    useEffect(() => {
        let ignore = false
        async function loadOrgOptions() {
            try {
                const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
                const data = res?.data?.data ?? res?.data ?? []
                if (!ignore) setOrgOptions(Array.isArray(data) ? data : [])
            } catch {
                if (!ignore) setOrgOptions([])
            }
        }
        loadOrgOptions()
        return () => { ignore = true }
    }, [])
    // 데이터 로딩 함수
    const loadDashboardData = useCallback(async () => {
        setLoading(true)
        try {
            const params = { periodType: 'week' } // 대시보드는 항상 "현재 주간" 기준
            if (orgId) params.orgId = orgId

            const [kpiRes, seriesRes] = await Promise.all([
                api.get('/dashboard/kpi', { params }),
                api.get('/dashboard/timeseries', { params }),
            ])

            const kpiData = kpiRes.data?.data || {}
            setKpi({
                new: kpiData.newCount ?? '-',
                fix: kpiData.fixCount ?? '-',
                done: kpiData.doneCount ?? '-',
                avgDays: kpiData.avgLeadDays ?? '-',
                total: kpiData.totalRequests ?? '-',
                pending: kpiData.pendingCount ?? '-',
                inProgress: kpiData.inProgressCount ?? '-',
                completed: kpiData.completedCount ?? '-',
                completionRate: kpiData.completionRate ?? '-'
            })

            setSeries(Array.isArray(seriesRes.data?.data) ? seriesRes.data.data : [])
        } catch (error) {
            console.error('Failed to load dashboard data:', error)
        } finally {
            setLoading(false)
        }
    }, [orgId])

    useEffect(() => {
        loadDashboardData()
    }, [loadDashboardData])

    // 공지 카운트 로딩(대시보드 카드)
    useEffect(() => {
        let ignore = false
        async function loadNoticeCounts() {
            try {
                const res = await api.get('/notices/dashboard-counts')
                const payload = res?.data?.data ?? res?.data ?? {}
                const internalUnread = payload.internalUnreadCount ?? 0
                const insurerUnread = payload.insurerUnreadCount ?? 0
                const appr = payload.approvalNeededCount ?? 0
                if (!ignore) {
                    setNoticeInternalUnreadCount(Number(internalUnread) || 0)
                    setNoticeInsurerUnreadCount(Number(insurerUnread) || 0)
                    setNoticeApprovalNeededCount(Number(appr) || 0)
                }
            } catch {
                if (!ignore) {
                    setNoticeInternalUnreadCount(0)
                    setNoticeInsurerUnreadCount(0)
                    setNoticeApprovalNeededCount(0)
                }
            }
        }
        loadNoticeCounts()
        return () => { ignore = true }
    }, [canApproveNotice])

    useEffect(() => {
        let ignore = false
        async function loadActive() {
            try {
                if (!canViewActiveStatus) return
                setActiveLoading(true)
                const me = await api.get('/users/me')
                const myOrgId = (me?.data?.data?.orgId ?? me?.data?.orgId) || undefined
                const targetOrgId = orgId || myOrgId
                if (!targetOrgId) return

                // 전체 유저: 상태 구분 없이 해당 조직의 현재 근무 상태 조회
                const res = await api.get('/manager-status/search', { params: { orgId: targetOrgId } })
                const data = res?.data?.data ?? res?.data ?? []
                if (!ignore) setActiveOnDuty(Array.isArray(data) ? data : [])
            } catch { } finally {
                setActiveLoading(false)
            }
        }
        loadActive()
        return () => { ignore = true }
    }, [orgId, canViewActiveStatus])

    useEffect(() => {
        let ignore = false
        async function loadSignupPending() {
            try {
                if (!canViewActiveStatus) return
                const me = await api.get('/users/me')
                const myOrgId = (me?.data?.data?.orgId ?? me?.data?.orgId) || undefined
                const targetOrgId = orgId || myOrgId
                if (!targetOrgId) return
                const res = await api.get('/signups/pending-count', { params: { orgId: targetOrgId } })
                const cnt = res?.data?.data?.count ?? res?.data?.count ?? 0
                if (!ignore) setSignupPendingCount(Number(cnt) || 0)
            } catch {
                if (!ignore) setSignupPendingCount(0)
            }
        }
        loadSignupPending()
        return () => { ignore = true }
    }, [orgId, canViewActiveStatus])

    // SSE 기반 웹알림(실시간 알림) → NotificationCenter에서 가져와 대시보드 알림으로 표시
    const alerts = useMemo(() => {
        if (!Array.isArray(notifications) || notifications.length === 0) return []
        return notifications.map((n, idx) => {
            const payload = n.payload || {}
            const type = payload.type || (payload.test ? 'test' : 'info')
            let title = payload.title || '알림'
            let message = ''
            let targetPath = null

            if (payload.type === 'SIGNUP_REQUEST_CREATED') {
                title = '가입 신청 접수'
                message = `${payload.orgName || ''}에 ${payload.name || payload.email || ''} 님의 가입 신청이 접수되었습니다.`.trim()
                if (payload.signupId) {
                    targetPath = `/signups/${payload.signupId}`
                } else {
                    targetPath = '/signups'
                }
            } else if (payload.type === 'SIGNUP_APPROVED') {
                title = '가입 신청 승인'
                message = '가입 신청이 승인되었습니다.'
                targetPath = '/profile'
            } else if (payload.type === 'SIGNUP_REJECTED') {
                title = '가입 신청 반려'
                message = '가입 신청이 반려되었습니다.'
                targetPath = '/profile'
            } else if (payload.insurerOrgName || payload.requestId) {
                // 의뢰 생성/배정 계열
                title = payload.title || '의뢰 알림'
                if (payload.insurerOrgName) {
                    message = `${payload.insurerOrgName}에 의뢰가 배정되었습니다.`
                } else if (payload.requestId) {
                    message = `의뢰 #${payload.requestId} 관련 알림입니다.`
                }
                if (payload.requestId !== null && payload.requestId !== undefined && payload.requestId !== 0) {
                    targetPath = `/requests/${payload.requestId}`
                } else {
                    // 테스트 알림 등 requestId가 유효하지 않은 경우 목록으로 이동
                    targetPath = '/requests'
                }
            } else if (payload.type === 'NOTICE_APPROVAL_NEEDED' && payload.noticeId) {
                title = '공지 승인 필요'
                message = payload.title ? `승인 필요: ${payload.title}` : '승인이 필요한 공지가 있습니다.'
                targetPath = `/notices/${payload.noticeId}`
            } else if (payload.type === 'NOTICE_APPROVED' && payload.noticeId) {
                title = '새 공지'
                message = payload.title ? `새 공지: ${payload.title}` : '새 공지가 등록되었습니다.'
                targetPath = `/notices/${payload.noticeId}`
            } else {
                message = Object.keys(payload).length > 0 ? JSON.stringify(payload) : ''
            }

            return {
                id: n.id || `${n.receivedAt}-${idx}`,
                type: type === 'warning' || type === 'error' || type === 'info' ? type : 'info',
                title,
                message,
                details: payload.test ? '테스트 알림입니다.' : null,
                createdAt: n.receivedAt,
                targetPath,
            }
        })
    }, [notifications])

    // 알림 페이징 (최신순, 페이지당 5개)
    const [alertPage, setAlertPage] = useState(0)
    const pageSize = 5
    const totalPages = alerts.length > 0 ? Math.ceil(alerts.length / pageSize) : 0
    const pagedAlerts = alerts.slice(alertPage * pageSize, alertPage * pageSize + pageSize)
    // 파이 차트 데이터 준비
    const pieData = [
        { name: '대기', value: Number(kpi.pending) || 0, color: '#F59E0B' },
        { name: '진행중', value: Number(kpi.inProgress) || 0, color: '#38BDF8' },
        { name: '완료', value: Number(kpi.completed) || 0, color: '#2DD4BF' },
        { name: '보완', value: Number(kpi.fix) || 0, color: '#EF4444' }
    ].filter(item => item.value > 0)

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">대시보드</h1>
                    <p className="text-slate-600 mt-2">현재 현황 중심의 요약 화면입니다.</p>
                </div>
                {loading && (
                    <Loading label="데이터 로딩 중..." />
                )}
            </div>

            {/* 조직 선택: 현재 기준 조직 */}
            <div className="mt-4 flex items-center gap-2">
                <span className="text-sm text-slate-700">조직 선택</span>
                <select
                    className="rounded border px-3 py-2 text-sm"
                    value={orgId || ''}
                    onChange={(e) => setOrgId(e.target.value || '')}
                >
                    <option value="">전체</option>
                    {orgOptions.map(o => (
                        <option key={o.id} value={o.id}>
                            {o.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* 1) 관리자/리더/총무용 레이아웃 */}
            {isAdminView && (
                <>
                    {/* 근무 상태 (선택 조직 기준 전체 현황) */}
                    {canViewActiveStatus && (
                        <div className="rounded border p-4 mt-6">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-lg font-semibold">근무 상태</h3>
                                <p className="text-xs text-slate-500">현재 선택된 조직의 인원 현황 요약</p>
                            </div>
                            {activeLoading ? (
                                <Loading />
                            ) : (
                                (() => {
                                    const byStatus = activeOnDuty.reduce((acc, cur) => {
                                        const k = cur.status || 'ON_DUTY'
                                        acc[k] = (acc[k] || 0) + 1
                                        return acc
                                    }, {})
                                    const total = activeOnDuty.length
                                    const on = byStatus.ON_DUTY || 0
                                    const vac = byStatus.VACATION || 0
                                    const tr = byStatus.TRAINING || 0
                                    const away = byStatus.AWAY || 0
                                    return (
                                        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                                            <button
                                                type="button"
                                                className="rounded bg-slate-50 p-3 text-left hover:bg-slate-100"
                                                onClick={() => {
                                                    const target = orgId || ''
                                                    navigate(target ? `/users?orgId=${target}` : '/users')
                                                }}
                                            >
                                                <div className="text-xs font-medium text-slate-600">전체 인원 수</div>
                                                <div className="mt-1 text-2xl font-semibold text-slate-800">{total}</div>
                                            </button>
                                            <button
                                                type="button"
                                                className="rounded bg-emerald-50 p-3 text-left hover:bg-emerald-100"
                                                onClick={() => {
                                                    const target = orgId || ''
                                                    navigate(target ? `/users?orgId=${target}&availabilityStatus=ON_DUTY` : '/users?availabilityStatus=ON_DUTY')
                                                }}
                                            >
                                                <div className="text-xs font-medium text-emerald-700">근무중 인원</div>
                                                <div className="mt-1 text-2xl font-semibold text-emerald-800">{on}</div>
                                            </button>
                                            <button
                                                type="button"
                                                className="rounded bg-amber-50 p-3 text-left hover:bg-amber-100"
                                                onClick={() => {
                                                    const target = orgId || ''
                                                    navigate(target ? `/users?orgId=${target}&availabilityStatus=VACATION` : '/users?availabilityStatus=VACATION')
                                                }}
                                            >
                                                <div className="text-xs font-medium text-amber-700">휴가 인원</div>
                                                <div className="mt-1 text-2xl font-semibold text-amber-800">{vac}</div>
                                            </button>
                                            <button
                                                type="button"
                                                className="rounded bg-sky-50 p-3 text-left hover:bg-sky-100"
                                                onClick={() => {
                                                    const target = orgId || ''
                                                    navigate(target ? `/users?orgId=${target}&availabilityStatus=TRAINING` : '/users?availabilityStatus=TRAINING')
                                                }}
                                            >
                                                <div className="text-xs font-medium text-sky-700">교육중 인원</div>
                                                <div className="mt-1 text-2xl font-semibold text-sky-800">{tr}</div>
                                            </button>
                                            <button
                                                type="button"
                                                className="rounded bg-slate-50 p-3 text-left hover:bg-slate-100"
                                                onClick={() => {
                                                    const target = orgId || ''
                                                    navigate(target ? `/users?orgId=${target}&availabilityStatus=AWAY` : '/users?availabilityStatus=AWAY')
                                                }}
                                            >
                                                <div className="text-xs font-medium text-slate-600">부재중 인원</div>
                                                <div className="mt-1 text-2xl font-semibold text-slate-800">{away}</div>
                                            </button>
                                            <button
                                                type="button"
                                                className="rounded bg-violet-50 p-3 text-left hover:bg-violet-100"
                                                onClick={() => navigate('/signups')}
                                            >
                                                <div className="text-xs font-medium text-violet-700">가입 승인 대기 인원</div>
                                                <div className="mt-1 text-2xl font-semibold text-violet-800">{signupPendingCount}</div>
                                            </button>
                                        </div>
                                    )
                                })()
                            )}
                        </div>
                    )}

                    {/* 의뢰 상태 요약 섹션 */}
                    <div className="rounded border p-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">의뢰 상태 요약</h3>
                            <p className="text-xs text-slate-500">현재 상태별 의뢰 개수 요약</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                            <div className="rounded bg-slate-50 p-3">
                                <div className="text-xs font-medium text-slate-600">총 의뢰</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.total}</div>
                            </div>
                            <div className="rounded bg-amber-50 p-3">
                                <div className="text-xs font-medium text-amber-700">대기</div>
                                <div className="mt-1 text-2xl font-semibold text-amber-800">{kpi.pending}</div>
                            </div>
                            <div className="rounded bg-sky-50 p-3">
                                <div className="text-xs font-medium text-sky-700">진행중</div>
                                <div className="mt-1 text-2xl font-semibold text-sky-800">{kpi.inProgress}</div>
                            </div>
                            <div className="rounded bg-emerald-50 p-3">
                                <div className="text-xs font-medium text-emerald-700">완료율</div>
                                <div className="mt-1 text-2xl font-semibold text-emerald-800">{kpi.completionRate}%</div>
                            </div>
                        </div>
                    </div>

                    {/* 공지 요약 */}
                    <div className="rounded border p-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">공지</h3>
                            <p className="text-xs text-slate-500">우리조직/원수사 공지</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                            <div className="rounded bg-slate-50 p-3">
                                <div className="text-sm font-semibold text-slate-800">우리조직 내 공지</div>
                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        className="rounded bg-white p-3 text-left hover:bg-slate-50 border"
                                        onClick={() => navigate(myNoticeOrgType ? `/notices?noticeOrgType=${myNoticeOrgType}&status=APPROVED` : '/notices')}
                                    >
                                        <div className="text-xs font-medium text-slate-600">신규 공지(미읽음)</div>
                                        <div className="mt-1 text-2xl font-semibold text-slate-900">{noticeInternalUnreadCount}</div>
                                    </button>
                                    {canApproveNotice && (
                                        <button
                                            type="button"
                                            className="rounded bg-white p-3 text-left hover:bg-slate-50 border"
                                            onClick={() => navigate(myNoticeOrgType ? `/notices?noticeOrgType=${myNoticeOrgType}&status=DRAFT` : '/notices?status=DRAFT')}
                                        >
                                            <div className="text-xs font-medium text-slate-600">승인 필요 공지</div>
                                            <div className="mt-1 text-2xl font-semibold text-slate-900">{noticeApprovalNeededCount}</div>
                                        </button>
                                    )}
                                </div>
                            </div>
                            {(noticeInsurerUnreadCount > 0 || isGaAdmin || isGaPlannerView) && (
                                <div className="rounded bg-slate-50 p-3">
                                    <div className="text-sm font-semibold text-slate-800">원수사 공지</div>
                                    <div className="mt-3 grid grid-cols-1 gap-3">
                                        <button
                                            type="button"
                                            className="rounded bg-white p-3 text-left hover:bg-slate-50 border"
                                            onClick={() => navigate('/notices?noticeOrgType=INSURER&status=APPROVED')}
                                        >
                                            <div className="text-xs font-medium text-slate-600">신규 공지(미읽음)</div>
                                            <div className="mt-1 text-2xl font-semibold text-slate-900">{noticeInsurerUnreadCount}</div>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 알림 카드 */}
                    {alerts.length > 0 && (
                        <div className="mt-6">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-semibold">알림</h3>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2 text-xs text-slate-600">
                                        <button
                                            type="button"
                                            className="px-2 py-1 rounded border disabled:opacity-40"
                                            disabled={alertPage === 0}
                                            onClick={() => setAlertPage((p) => Math.max(0, p - 1))}
                                        >
                                            이전
                                        </button>
                                        <span>{alertPage + 1} / {totalPages}</span>
                                        <button
                                            type="button"
                                            className="px-2 py-1 rounded border disabled:opacity-40"
                                            disabled={alertPage >= totalPages - 1}
                                            onClick={() => setAlertPage((p) => Math.min(totalPages - 1, p + 1))}
                                        >
                                            다음
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {pagedAlerts.map(alert => (
                                    <div
                                        key={alert.id}
                                        className={`relative rounded border p-4 cursor-pointer transition hover:shadow-sm ${alert.type === 'warning' ? 'border-yellow-200 bg-yellow-50' :
                                            alert.type === 'error' ? 'border-red-200 bg-red-50' :
                                                alert.type === 'info' ? 'border-blue-200 bg-blue-50' :
                                                    'border-gray-200 bg-gray-50'
                                            }`}
                                        onClick={() => {
                                            if (alert.targetPath) {
                                                navigate(alert.targetPath)
                                            }
                                        }}
                                    >
                                        {/* 개별 알림 닫기 버튼 */}
                                        <button
                                            type="button"
                                            className="absolute top-2 right-2 text-xs text-slate-400 hover:text-slate-700"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                dismiss(alert.id)
                                            }}
                                        >
                                            ×
                                        </button>
                                        <div className="flex items-start gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${alert.type === 'warning' ? 'bg-yellow-200 text-yellow-800' :
                                                alert.type === 'error' ? 'bg-red-200 text-red-800' :
                                                    alert.type === 'info' ? 'bg-blue-200 text-blue-800' :
                                                        'bg-gray-200 text-gray-800'
                                                }`}>
                                                {alert.type === 'warning' ? '⚠️' :
                                                    alert.type === 'error' ? '❌' :
                                                        alert.type === 'info' ? 'ℹ️' : '📢'}
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-medium text-sm">{alert.title}</div>
                                                <div className="text-xs text-slate-600 mt-1">{alert.message}</div>
                                                {alert.details && (
                                                    <div className="text-xs text-slate-500 mt-2">
                                                        {alert.details}
                                                    </div>
                                                )}
                                                <div className="text-xs text-slate-400 mt-2">
                                                    {formatDateTimeMinute(alert.createdAt)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* GA 사용자는 KPI/상세지표/의뢰추이 노출 제외 */}
                    {!isGaUser && (
                        <>
                            {/* 요약 KPI 섹션 */}
                            <div className="rounded border p-4 mt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-lg font-semibold">요약 KPI</h3>
                                    <p className="text-xs text-slate-500">선택된 조직의 주간 의뢰 처리 현황 요약</p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                                    <div className="rounded bg-slate-50 p-3">
                                        <div className="text-xs font-medium text-slate-600">신규 의뢰</div>
                                        <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.new}</div>
                                    </div>
                                    <div className="rounded bg-slate-50 p-3">
                                        <div className="text-xs font-medium text-slate-600">보완 요청</div>
                                        <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.fix}</div>
                                    </div>
                                    <div className="rounded bg-slate-50 p-3">
                                        <div className="text-xs font-medium text-slate-600">완료</div>
                                        <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.done}</div>
                                    </div>
                                    <div className="rounded bg-slate-50 p-3">
                                        <div className="text-xs font-medium text-slate-600">평균 처리기간</div>
                                        <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.avgDays}일</div>
                                    </div>
                                </div>
                            </div>

                            {/* 상세 지표 (주간) */}
                            <div className="rounded border p-4 mt-6">
                                <h3 className="text-lg font-semibold mb-4">상세 지표 (주간)</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div className="text-center p-3 bg-slate-50 rounded">
                                        <div className="text-slate-600">처리율</div>
                                        <div className="text-xl font-semibold mt-1">{kpi.completionRate}%</div>
                                    </div>
                                    <div className="text-center p-3 bg-slate-50 rounded">
                                        <div className="text-slate-600">평균 처리일</div>
                                        <div className="text-xl font-semibold mt-1">{kpi.avgDays}일</div>
                                    </div>
                                    <div className="text-center p-3 bg-slate-50 rounded">
                                        <div className="text-slate-600">진행률</div>
                                        <div className="text-xl font-semibold mt-1">
                                            {kpi.total !== '-' ? Math.round(((Number(kpi.inProgress) + Number(kpi.completed)) / Number(kpi.total)) * 100) : 0}%
                                        </div>
                                    </div>
                                    <div className="text-center p-3 bg-slate-50 rounded">
                                        <div className="text-slate-600">보완률</div>
                                        <div className="text-xl font-semibold mt-1">
                                            {kpi.total !== '-' ? Math.round((Number(kpi.fix) / Number(kpi.total)) * 100) : 0}%
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 트렌드/분포 (주간) = 의뢰추이 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        {/* 시계열 차트 */}
                        <div className="rounded border p-4 h-72">
                            <h3 className="text-lg font-semibold mb-4">의뢰 추이 (주간)</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={series}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Area type="monotone" dataKey="newCount" stackId="1" stroke="#1E3A8A" fill="#1E3A8A" fillOpacity={0.6} />
                                    <Area type="monotone" dataKey="doneCount" stackId="1" stroke="#38BDF8" fill="#38BDF8" fillOpacity={0.6} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* 파이 차트 */}
                        <div className="rounded border p-4 h-72">
                            <h3 className="text-lg font-semibold mb-4">상태별 분포</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                        </>
                    )}
                </>
            )}

            {/* 2) GA 설계사용 레이아웃 */}
            {isGaPlannerView && (
                <>
                    {/* 내 의뢰 요약 섹션 */}
                    <div className="rounded border p-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">내 의뢰 요약</h3>
                            <p className="text-xs text-slate-500">현재 주간 기준 내 의뢰 상태 요약</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div className="rounded bg-slate-50 p-3">
                                <div className="text-xs font-medium text-slate-600">내 의뢰 - 신규/보완</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.new} / {kpi.fix}</div>
                            </div>
                            <div className="rounded bg-sky-50 p-3">
                                <div className="text-xs font-medium text-sky-700">내 의뢰 - 진행중</div>
                                <div className="mt-1 text-2xl font-semibold text-sky-800">{kpi.inProgress}</div>
                            </div>
                            <div className="rounded bg-emerald-50 p-3">
                                <div className="text-xs font-medium text-emerald-700">내 의뢰 - 완료</div>
                                <div className="mt-1 text-2xl font-semibold text-emerald-800">{kpi.done}</div>
                            </div>
                        </div>
                    </div>

                    {/* 내 알림 */}
                    {alerts.length > 0 && (
                        <div className="mt-6">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-semibold">내 알림</h3>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2 text-xs text-slate-600">
                                        <button
                                            type="button"
                                            className="px-2 py-1 rounded border disabled:opacity-40"
                                            disabled={alertPage === 0}
                                            onClick={() => setAlertPage((p) => Math.max(0, p - 1))}
                                        >
                                            이전
                                        </button>
                                        <span>{alertPage + 1} / {totalPages}</span>
                                        <button
                                            type="button"
                                            className="px-2 py-1 rounded border disabled:opacity-40"
                                            disabled={alertPage >= totalPages - 1}
                                            onClick={() => setAlertPage((p) => Math.min(totalPages - 1, p + 1))}
                                        >
                                            다음
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {pagedAlerts.map(alert => (
                                    <div
                                        key={alert.id}
                                        className="relative rounded border p-4 cursor-pointer transition hover:shadow-sm border-blue-100 bg-blue-50"
                                        onClick={() => {
                                            if (alert.targetPath) {
                                                navigate(alert.targetPath)
                                            }
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className="absolute top-2 right-2 text-xs text-slate-400 hover:text-slate-700"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                dismiss(alert.id)
                                            }}
                                        >
                                            ×
                                        </button>
                                        <div className="flex-1">
                                            <div className="font-medium text-sm">{alert.title}</div>
                                            <div className="text-xs text-slate-600 mt-1">{alert.message}</div>
                                            <div className="text-xs text-slate-400 mt-2">
                                                {formatDateTimeMinute(alert.createdAt)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 상태별 분포 */}
                    <div className="rounded border p-4 h-72 mt-6">
                        <h3 className="text-lg font-semibold mb-4">상태별 분포</h3>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </>
            )}

            {/* 3) 원수사 매니저용 레이아웃 */}
            {isInsManagerView && (
                <>
                    {/* 내 의뢰 요약 섹션 */}
                    <div className="rounded border p-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">내 의뢰 요약</h3>
                            <p className="text-xs text-slate-500">현재 주간 기준 담당 의뢰 상태 요약</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                            <div className="rounded bg-slate-50 p-3">
                                <div className="text-xs font-medium text-slate-600">신규 의뢰</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.new}</div>
                            </div>
                            <div className="rounded bg-rose-50 p-3">
                                <div className="text-xs font-medium text-rose-700">보완</div>
                                <div className="mt-1 text-2xl font-semibold text-rose-800">{kpi.fix}</div>
                            </div>
                            <div className="rounded bg-sky-50 p-3">
                                <div className="text-xs font-medium text-sky-700">진행중</div>
                                <div className="mt-1 text-2xl font-semibold text-sky-800">{kpi.inProgress}</div>
                            </div>
                            <div className="rounded bg-emerald-50 p-3">
                                <div className="text-xs font-medium text-emerald-700">완료</div>
                                <div className="mt-1 text-2xl font-semibold text-emerald-800">{kpi.done}</div>
                            </div>
                        </div>
                    </div>

                    {/* 내 알림 */}
                    {alerts.length > 0 && (
                        <div className="mt-6">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-semibold">내 알림</h3>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2 text-xs text-slate-600">
                                        <button
                                            type="button"
                                            className="px-2 py-1 rounded border disabled:opacity-40"
                                            disabled={alertPage === 0}
                                            onClick={() => setAlertPage((p) => Math.max(0, p - 1))}
                                        >
                                            이전
                                        </button>
                                        <span>{alertPage + 1} / {totalPages}</span>
                                        <button
                                            type="button"
                                            className="px-2 py-1 rounded border disabled:opacity-40"
                                            disabled={alertPage >= totalPages - 1}
                                            onClick={() => setAlertPage((p) => Math.min(totalPages - 1, p + 1))}
                                        >
                                            다음
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {pagedAlerts.map(alert => (
                                    <div
                                        key={alert.id}
                                        className="relative rounded border p-4 cursor-pointer transition hover:shadow-sm border-slate-200 bg-slate-50"
                                        onClick={() => {
                                            if (alert.targetPath) {
                                                navigate(alert.targetPath)
                                            }
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className="absolute top-2 right-2 text-xs text-slate-400 hover:text-slate-700"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                dismiss(alert.id)
                                            }}
                                        >
                                            ×
                                        </button>
                                        <div className="flex-1">
                                            <div className="font-medium text-sm">{alert.title}</div>
                                            <div className="text-xs text-slate-600 mt-1">{alert.message}</div>
                                            <div className="text-xs text-slate-400 mt-2">
                                                {formatDateTimeMinute(alert.createdAt)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 의뢰 추이(주간) + 상태별 분포 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        <div className="rounded border p-4 h-72">
                            <h3 className="text-lg font-semibold mb-4">의뢰 추이 (주간)</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={series}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Area type="monotone" dataKey="newCount" stackId="1" stroke="#1E3A8A" fill="#1E3A8A" fillOpacity={0.6} />
                                    <Area type="monotone" dataKey="doneCount" stackId="1" stroke="#38BDF8" fill="#38BDF8" fillOpacity={0.6} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="rounded border p-4 h-72">
                            <h3 className="text-lg font-semibold mb-4">상태별 분포</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* 상세 지표 (주간) */}
                    <div className="rounded border p-4 mt-6">
                        <h3 className="text-lg font-semibold mb-4">상세 지표 (주간)</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="text-center p-3 bg-slate-50 rounded">
                                <div className="text-slate-600">처리율</div>
                                <div className="text-xl font-semibold mt-1">{kpi.completionRate}%</div>
                            </div>
                            <div className="text-center p-3 bg-slate-50 rounded">
                                <div className="text-slate-600">평균 처리일</div>
                                <div className="text-xl font-semibold mt-1">{kpi.avgDays}일</div>
                            </div>
                            <div className="text-center p-3 bg-slate-50 rounded">
                                <div className="text-slate-600">진행률</div>
                                <div className="text-xl font-semibold mt-1">
                                    {kpi.total !== '-' ? Math.round(((Number(kpi.inProgress) + Number(kpi.completed)) / Number(kpi.total)) * 100) : 0}%
                                </div>
                            </div>
                            <div className="text-center p-3 bg-slate-50 rounded">
                                <div className="text-slate-600">보완률</div>
                                <div className="text-xl font-semibold mt-1">
                                    {kpi.total !== '-' ? Math.round((Number(kpi.fix) / Number(kpi.total)) * 100) : 0}%
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    )
}


